import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  Grid,
  Button,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Divider,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  Description as DescriptionIcon,
  FormatListNumbered as FormatListNumberedIcon,
  Notes as NotesIcon,
  CloudUpload as UploadIcon,
  PowerSettingsNew as PowerSettingsNewIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  RestartAlt as RestartAltIcon,
} from "@mui/icons-material";

import { useGetDocumentConfig } from "../../../hooks/useGetDocumentConfig";
import { useSaveDocumentConfig } from "../../../hooks/useSaveDocumentConfig";
import { useGetDateFormats } from "../../../hooks/panelEditor/useGetDateFormats";
import { AVAILABLE_FONTS } from "./globalStylesSection/constants/styleDefaults";

const MARGIN_FIELDS = [
  { key: "top", label: "Superior" },
  { key: "bottom", label: "Inferior" },
  { key: "left", label: "Izquierdo" },
  { key: "right", label: "Derecho" },
];

const DATE_FONT_SIZE_MIN = 6;
const DATE_FONT_SIZE_MAX = 72;
const DATE_FONT_SIZE_START = 10;

const sizeButtonSx = {
  bgcolor: "#e5e7eb",
  borderRadius: "50%",
  "&:hover": { bgcolor: "#d1d5db" },
};

const EMPTY_FORM = {
  margins: {},
  dateFormat: "DEFAULT",
  dateFontFamily: null,
  dateFontSize: null,
  pageNumberingEnabled: true,
  noContentLegendActive: true,
  titleWithWitnesses: false,
  flgActive: true,
};

const cloneForm = (source) => ({
  margins: { ...(source?.margins || {}) },
  dateFormat: source?.dateFormat ?? "DEFAULT",
  dateFontFamily: source?.dateFontFamily ?? null,
  dateFontSize:
    source?.dateFontSize !== undefined && source?.dateFontSize !== null
      ? Number(source.dateFontSize)
      : null,
  pageNumberingEnabled:
    source?.pageNumberingEnabled !== undefined
      ? Boolean(source.pageNumberingEnabled)
      : true,
  noContentLegendActive:
    source?.noContentLegendActive !== undefined
      ? Boolean(source.noContentLegendActive)
      : true,
  titleWithWitnesses:
    source?.titleWithWitnesses !== undefined
      ? Boolean(source.titleWithWitnesses)
      : true,
  flgActive: source?.flgActive !== undefined ? Boolean(source.flgActive) : true,
});

const computeDiff = (original, current) => {
  if (!original) return current;
  return Object.entries(current).reduce((acc, [key, value]) => {
    const originalValue = original[key];
    const isDifferent =
      typeof value === "object" && value !== null
        ? JSON.stringify(value) !== JSON.stringify(originalValue)
        : value !== originalValue;
    if (isDifferent) acc[key] = value;
    return acc;
  }, {});
};

export const DocumentConfigPanel = ({ token, idDocument }) => {
  const { loading, fetchConfig } = useGetDocumentConfig(token);
  const { saveConfig, saving } = useSaveDocumentConfig(token);
  const { dateFormats, loading: loadingFormats } = useGetDateFormats(token);

  const [form, setForm] = useState(EMPTY_FORM);
  const [originalConfig, setOriginalConfig] = useState(EMPTY_FORM);
  const [hasActiveConfig, setHasActiveConfig] = useState(false);

  useEffect(() => {
    if (!idDocument) return;
    fetchConfig(idDocument).then((fetched) => {
      if (fetched) {
        const merged = cloneForm(fetched);
        setForm(merged);
        setOriginalConfig(merged);
        setHasActiveConfig(true);
      } else {
        setForm(EMPTY_FORM);
        setOriginalConfig(EMPTY_FORM);
        setHasActiveConfig(false);
      }
    });
  }, [idDocument, fetchConfig]);

  const setField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const setMarginField = useCallback((key, rawValue) => {
    setForm((prev) => {
      const nextMargins = { ...prev.margins };
      if (rawValue === "") {
        delete nextMargins[key];
      } else {
        const numericValue = Number(rawValue);
        if (!Number.isNaN(numericValue)) nextMargins[key] = numericValue;
      }
      return { ...prev, margins: nextMargins };
    });
  }, []);

  const changeDateFontSize = useCallback((step) => {
    setForm((prev) => {
      const current = prev.dateFontSize ?? DATE_FONT_SIZE_START - step;
      const next = Math.min(
        DATE_FONT_SIZE_MAX,
        Math.max(DATE_FONT_SIZE_MIN, current + step)
      );
      return { ...prev, dateFontSize: next };
    });
  }, []);

  const handleSave = async () => {
    const diff = computeDiff(originalConfig, form);

    if (diff.margins) {
      const cleanedMargins = Object.fromEntries(
        Object.entries(diff.margins).filter(
          ([, value]) => value !== undefined && value !== null && value !== ""
        )
      );
      if (Object.keys(cleanedMargins).length > 0) {
        diff.margins = cleanedMargins;
      } else {
        delete diff.margins;
      }
    }

    const willBeActive = form.flgActive !== false;

    if (Object.keys(diff).length > 0 && diff.flgActive === undefined) {
      diff.flgActive = willBeActive;
    }

    if (willBeActive && Object.keys(diff).length > 0) {
      if (diff.dateFormat === undefined) diff.dateFormat = form.dateFormat;
      if (diff.pageNumberingEnabled === undefined) {
        diff.pageNumberingEnabled = form.pageNumberingEnabled;
      }
      if (diff.noContentLegendActive === undefined) {
        diff.noContentLegendActive = form.noContentLegendActive;
      }
      if (diff.titleWithWitnesses === undefined) {
        diff.titleWithWitnesses = form.titleWithWitnesses;
      }
    }

    const success = await saveConfig(idDocument, diff);
    if (success) {
      if (willBeActive) {
        setOriginalConfig({ ...form });
      } else {
        const cleared = { ...EMPTY_FORM, flgActive: false };
        setForm(cleared);
        setOriginalConfig(cleared);
      }
      setHasActiveConfig(true);
    }
  };

  const hasDiff = Object.keys(computeDiff(originalConfig, form)).length > 0;

  return (
    <Card sx={{ borderRadius: "16px", bgcolor: "#ffffff" }}>
      <CardContent sx={{ p: 4 }}>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <DescriptionIcon sx={{ fontSize: 20, color: "#1976d2" }} />
          <Typography variant="h6" fontWeight="bold">
            Configuración de Generación del Word
          </Typography>
          {hasDiff && (
            <Chip
              label="Sin guardar"
              size="small"
              color="warning"
              variant="outlined"
              sx={{ fontSize: "0.65rem", height: 18 }}
            />
          )}
          {hasActiveConfig && !hasDiff && form.flgActive && (
            <Chip
              label="Personalizado"
              size="small"
              color="primary"
              variant="outlined"
              sx={{ fontSize: "0.65rem", height: 18 }}
            />
          )}
          {hasActiveConfig && !hasDiff && !form.flgActive && (
            <Chip
              label="Guardado (inactivo)"
              size="small"
              color="default"
              variant="outlined"
              sx={{ fontSize: "0.65rem", height: 18 }}
            />
          )}
          {loading && <CircularProgress size={14} />}
        </Box>

        <Typography variant="body2" color="text.secondary" mb={3}>
          Configura márgenes, formato y tipografía de la fecha, numeración de páginas y la
          leyenda de "sin información" que se usarán al generar el Word de
          este documento. Los campos que dejes sin definir mantienen el
          comportamiento predeterminado del template.
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Cargando configuración…
            </Typography>
          </Box>
        ) : (
          <Stack spacing={3}>
            <Tooltip
              title={
                form.flgActive
                  ? "Esta configuración personalizada se aplicará al generar el Word de este documento"
                  : "Esta configuración está desactivada; el documento se generará con los valores predeterminados del template"
              }
              placement="top"
              arrow
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  backgroundColor: form.flgActive ? "#e3f2fd" : "#f5f5f5",
                  borderRadius: "10px",
                  padding: "10px 16px",
                  border: `1px solid ${form.flgActive ? "#90caf9" : "#e0e0e0"}`,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <PowerSettingsNewIcon
                    sx={{
                      color: form.flgActive ? "#1976d2" : "text.disabled",
                      fontSize: 20,
                    }}
                  />
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    color={form.flgActive ? "primary" : "text.secondary"}
                  >
                    Configuración personalizada activa
                  </Typography>
                </Box>
                <Switch
                  checked={Boolean(form.flgActive)}
                  onChange={(e) => setField("flgActive", e.target.checked)}
                  color="primary"
                />
              </Box>
            </Tooltip>

            {form.flgActive && (
              <>
                <Divider />

                <Box>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{ color: "#455a7a", display: "block", mb: 1.5 }}
                  >
                    MÁRGENES (CM)
                  </Typography>
                  <Grid container spacing={2}>
                    {MARGIN_FIELDS.map(({ key, label }) => (
                      <Grid item xs={6} sm={3} key={key}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label={label}
                          value={form.margins?.[key] ?? ""}
                          onChange={(e) => setMarginField(key, e.target.value)}
                          inputProps={{ min: 0, max: 15, step: 0.1 }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">cm</InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>

                <Divider />

                <FormControl fullWidth size="small">
                  <InputLabel id="document-config-date-format-label">
                    Formato de fecha
                  </InputLabel>
                  <Select
                    labelId="document-config-date-format-label"
                    label="Formato de fecha"
                    value={form.dateFormat || "DEFAULT"}
                    onChange={(e) => setField("dateFormat", e.target.value)}
                    disabled={loadingFormats}
                  >
                    {!dateFormats?.some((item) => item.value === "DEFAULT") && (
                      <MenuItem value="DEFAULT">
                        <em>Predeterminado (comportamiento actual del template)</em>
                      </MenuItem>
                    )}
                    {dateFormats?.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                        {item.example ? ` — ${item.example}` : ""}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box
                  display="flex"
                  gap={2}
                  alignItems="center"
                  flexWrap="wrap"
                >
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel id="document-config-date-font-family-label">
                      Fuente de la fecha
                    </InputLabel>
                    <Select
                      labelId="document-config-date-font-family-label"
                      label="Fuente de la fecha"
                      value={form.dateFontFamily ?? ""}
                      onChange={(e) =>
                        setField("dateFontFamily", e.target.value || null)
                      }
                      sx={{ borderRadius: "16px" }}
                      renderValue={(value) => (
                        <span style={{ fontFamily: value }}>
                          {AVAILABLE_FONTS.find((f) => f.value === value)?.label ?? value}
                        </span>
                      )}
                    >
                      <MenuItem value="">
                        <em>Predeterminada</em>
                      </MenuItem>
                      {AVAILABLE_FONTS.map((font) => (
                        <MenuItem
                          key={font.value}
                          value={font.value}
                          sx={{ fontFamily: font.value }}
                        >
                          {font.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" color="text.secondary">
                      Tamaño
                    </Typography>
                    <Tooltip title="Disminuir tamaño">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => changeDateFontSize(-1)}
                          disabled={form.dateFontSize === DATE_FONT_SIZE_MIN}
                          sx={sizeButtonSx}
                        >
                          <RemoveIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Chip
                      label={form.dateFontSize ?? "Auto"}
                      size="small"
                      sx={{ minWidth: 48 }}
                    />
                    <Tooltip title="Aumentar tamaño">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => changeDateFontSize(1)}
                          disabled={form.dateFontSize === DATE_FONT_SIZE_MAX}
                          sx={sizeButtonSx}
                        >
                          <AddIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    {form.dateFontSize !== null && (
                      <Tooltip title="Usar el tamaño del template">
                        <IconButton
                          size="small"
                          onClick={() => setField("dateFontSize", null)}
                        >
                          <RestartAltIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>

                <Divider />

                <Tooltip
                  title={
                    form.pageNumberingEnabled
                      ? "El documento incluirá número de página en cada hoja"
                      : "El documento se generará sin numeración de páginas"
                  }
                  placement="top"
                  arrow
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      backgroundColor: form.pageNumberingEnabled
                        ? "#e3f2fd"
                        : "#f5f5f5",
                      borderRadius: "10px",
                      padding: "10px 16px",
                      border: `1px solid ${
                        form.pageNumberingEnabled ? "#90caf9" : "#e0e0e0"
                      }`,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <FormatListNumberedIcon
                        sx={{
                          color: form.pageNumberingEnabled
                            ? "#1976d2"
                            : "text.disabled",
                          fontSize: 20,
                        }}
                      />
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color={
                          form.pageNumberingEnabled ? "primary" : "text.secondary"
                        }
                      >
                        Numeración de páginas
                      </Typography>
                    </Box>
                    <Switch
                      checked={Boolean(form.pageNumberingEnabled)}
                      onChange={(e) =>
                        setField("pageNumberingEnabled", e.target.checked)
                      }
                      color="primary"
                    />
                  </Box>
                </Tooltip>

                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(form.noContentLegendActive)}
                      onChange={(e) =>
                        setField("noContentLegendActive", e.target.checked)
                      }
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": { color: "#1976d2" },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                          bgcolor: "#1976d2",
                        },
                      }}
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center" gap={1}>
                      <NotesIcon sx={{ fontSize: 18, color: "#6b7fa6" }} />
                      <Typography variant="body2">
                        Mostrar leyenda "No se reportó información."
                      </Typography>
                    </Box>
                  }
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(form.titleWithWitnesses)}
                      onChange={(e) =>
                        setField("titleWithWitnesses", e.target.checked)
                      }
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": { color: "#1976d2" },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                          bgcolor: "#1976d2",
                        },
                      }}
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center" gap={1}>
                      <NotesIcon sx={{ fontSize: 18, color: "#6b7fa6" }} />
                      <Typography variant="body2">
                        Enlazar la URL de los testigos en el título de la nota
                      </Typography>
                    </Box>
                  }
                />
              </>
            )}
          </Stack>
        )}

        {!loading && (
          <Grid container justifyContent="flex-end" spacing={2} sx={{ mt: 1 }}>
            <Grid item>
              <Button
                id="document-config-save-btn"
                variant="contained"
                onClick={handleSave}
                disabled={!hasDiff || saving}
                startIcon={<UploadIcon sx={{ fontSize: 20 }} />}
                sx={{
                  mt: 3,
                  bgcolor: "#1976d2",
                  color: "white",
                  borderRadius: "50px",
                  px: 4,
                  py: 1.5,
                  textTransform: "capitalize",
                  "&:hover": { bgcolor: "#1565c0" },
                  "&:disabled": {
                    bgcolor: "#e0e0e0",
                    color: "#9e9e9e",
                  },
                }}
              >
                {saving ? "Guardando…" : "Guardar Cambios"}
              </Button>
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

export default DocumentConfigPanel;
